import { Content, Header, Page } from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useCallback, useState } from 'react';

const REPO_URL = 'https://github.com/isolvedhcm/workstation-setup';

const FILES_TO_DOWNLOAD = [
  { name: 'run-setup.bat', type: 'application/octet-stream' },
  { name: 'workstation-setup.ps1', type: 'application/octet-stream' },
];

const TOOLS_INSTALLED = [
  'Git',
  'VS Code',
  'Visual Studio',
  'Build Tools',
  '.NET 10 & 8 SDKs',
  'Node.js',
  'SQL Server Express',
  'SSMS',
  'Claude Code',
];

const REPO_SETUP = [
  'SSH & Git configuration',
  'Repo cloning',
  'npm dependencies',
  'IIS configuration',
  'NuGet authentication',
  'Solution build',
];

const useStyles = makeStyles(theme => ({
  card: {
    maxWidth: 800,
  },
  stepNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
    fontSize: '0.875rem',
    marginRight: theme.spacing(1.5),
    flexShrink: 0,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
  stepTitle: {
    fontWeight: 600,
  },
  stepDetail: {
    marginLeft: 28 + theme.spacing(1.5),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  commandContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 2),
    fontFamily: '"Consolas", "Courier New", monospace',
    fontSize: '0.875rem',
    border: `1px solid ${theme.palette.divider}`,
    marginLeft: 28 + theme.spacing(1.5),
    marginTop: theme.spacing(1),
    maxWidth: 500,
  },
  commandText: {
    flexGrow: 1,
    userSelect: 'all',
    whiteSpace: 'nowrap',
  },
  downloadSection: {
    marginLeft: 28 + theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  auditCard: {
    maxWidth: 800,
    marginTop: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
  },
  auditHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

function CopyableCommand({ command }: { command: string }) {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [command]);

  return (
    <div className={classes.commandContainer}>
      <code className={classes.commandText}>{command}</code>
      <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
        <IconButton size="small" onClick={handleCopy}>
          {copied ? (
            <CheckIcon fontSize="small" color="primary" />
          ) : (
            <FileCopyIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
}

function triggerBlobDownload(content: string, filename: string) {
  const blob = new Blob([content], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const WorkstationSetupPage = () => {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string>();

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setError(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('workstation-setup');
      const results = await Promise.all(
        FILES_TO_DOWNLOAD.map(async file => {
          const response = await fetchApi.fetch(
            `${baseUrl}/files/${file.name}`,
          );
          if (!response.ok) {
            throw new Error(
              `Failed to download ${file.name}: ${response.statusText}`,
            );
          }
          const content = await response.text();
          if (content.trimStart().startsWith('<!')) {
            throw new Error(
              `${file.name} returned an HTML page instead of the expected file — the backend may be unreachable or misconfigured`,
            );
          }
          return { name: file.name, content };
        }),
      );
      for (const file of results) {
        triggerBlobDownload(file.content, file.name);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }, [discoveryApi, fetchApi]);

  return (
    <Page themeId="tool">
      <Header
        title="Workstation Setup"
        subtitle="Get your development environment ready"
      />
      <Content>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="body1" paragraph>
              This script installs and configures everything you need to start
              developing. It&apos;s safe to re-run at any time — it detects
              what&apos;s already installed and only fills in the gaps.
            </Typography>

            {/* What this installs */}
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Tools installed
            </Typography>
            <div className={classes.chipContainer}>
              {TOOLS_INSTALLED.map(tool => (
                <Chip key={tool} label={tool} size="small" variant="outlined" />
              ))}
            </div>

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Repo &amp; environment setup
            </Typography>
            <div className={classes.chipContainer}>
              {REPO_SETUP.map(item => (
                <Chip key={item} label={item} size="small" variant="outlined" />
              ))}
            </div>

            <Divider style={{ marginTop: 24, marginBottom: 8 }} />

            {/* Step 1 */}
            <div className={classes.stepHeader}>
              <span className={classes.stepNumber}>1</span>
              <Typography variant="subtitle1" className={classes.stepTitle}>
                Download the setup files
              </Typography>
            </div>
            <Typography variant="body2" className={classes.stepDetail}>
              Click the button below to download two files (
              <strong>run-setup.bat</strong> and{' '}
              <strong>workstation-setup.ps1</strong>) to your Downloads folder.
              Both files must be in the same folder.
            </Typography>
            <Box className={classes.downloadSection}>
              <Button
                variant="contained"
                color="primary"
                startIcon={downloading ? undefined : <GetAppIcon />}
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? 'Downloading...' : 'Download Setup Files'}
              </Button>
              {error && (
                <Typography
                  variant="body2"
                  color="error"
                  style={{ marginTop: 4 }}
                >
                  {error}
                </Typography>
              )}
            </Box>

            {/* Step 2 */}
            <div className={classes.stepHeader}>
              <span className={classes.stepNumber}>2</span>
              <Typography variant="subtitle1" className={classes.stepTitle}>
                Run the setup
              </Typography>
            </div>
            <Typography variant="body2" className={classes.stepDetail}>
              Open your <strong>Downloads</strong> folder in File Explorer, then
              right-click <strong>run-setup.bat</strong> and select{' '}
              <strong>Run as administrator</strong>.
            </Typography>
            <Typography variant="body2" className={classes.stepDetail}>
              If prompted with &quot;Do you want to allow this app to make
              changes to your device?&quot;, click <strong>Yes</strong>.
            </Typography>
            <Typography variant="body2" className={classes.stepDetail}>
              The script will walk you through the rest of the setup. Follow any
              prompts that appear on screen.
            </Typography>

            {/* Footer */}
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 24 }}
            >
              For more details and advanced options, see the{' '}
              <Link href={REPO_URL} target="_blank" rel="noopener">
                workstation-setup documentation on GitHub
              </Link>
              .
            </Typography>
          </CardContent>
        </Card>

        {/* Audit mode card */}
        <Card className={classes.auditCard} variant="outlined">
          <CardContent>
            <div className={classes.auditHeader}>
              <VisibilityIcon color="action" />
              <Typography variant="subtitle1" className={classes.stepTitle}>
                Already have some tools installed?
              </Typography>
            </div>
            <Typography variant="body2" color="textSecondary" paragraph>
              After downloading, you can run the script in{' '}
              <strong>audit mode</strong> to see what&apos;s already set up and
              what&apos;s missing — without installing anything. Open PowerShell
              as Administrator, navigate to your Downloads folder, and run:
            </Typography>
            <CopyableCommand command=".\workstation-setup.ps1 -Audit" />
          </CardContent>
        </Card>
      </Content>
    </Page>
  );
};
